// src/utils/networkRetry.js - New utility file
import { logger } from './logger';

export class NetworkError extends Error {
  constructor(message, type = 'network', retryable = true, statusCode = null) {
    super(message);
    this.name = 'NetworkError';
    this.type = type;
    this.retryable = retryable;
    this.statusCode = statusCode;
  }
}

// Advanced retry utility with exponential backoff and jitter
export async function fetchWithRetry(url, options = {}, config = {}) {
  const {
    maxRetries = 3,
    baseDelay = 1000, // 1 second
    maxDelay = 10000, // 10 seconds
    backoffMultiplier = 2,
    jitter = true,
    timeout = 15000, // 15 second timeout
    retryCondition = (error, response) => {
      // Retry on network errors or 5xx status codes
      if (error && (error.name === 'TypeError' || error.name === 'NetworkError')) {
        return true;
      }
      if (response && response.status >= 500) {
        return true;
      }
      // Retry on 429 (rate limit) but with longer delay
      if (response && response.status === 429) {
        return true;
      }
      return false;
    }
  } = config;

  let lastError = null;
  let lastResponse = null;

  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    try {
      logger.log(`Network request attempt ${attempt}/${maxRetries + 1} to ${url}`);
      
      // Add timeout to fetch options
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const requestOptions = {
        ...options,
        signal: controller.signal
      };

      const response = await fetch(url, requestOptions);
      clearTimeout(timeoutId);
      
      // If response is ok or non-retryable error, return it
      if (response.ok || !retryCondition(null, response)) {
        return response;
      }
      
      // Store response for potential retry
      lastResponse = response;
      
      // If this was the last attempt, return the response
      if (attempt === maxRetries + 1) {
        return response;
      }
      
    } catch (error) {
      clearTimeout(timeoutId);
      lastError = error;
      
      // Transform different error types
      if (error.name === 'AbortError') {
        lastError = new NetworkError(
          'Request timed out. Please check your connection and try again.',
          'timeout',
          true
        );
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        lastError = new NetworkError(
          'Network connection failed. Please check your internet connection.',
          'connection',
          true
        );
      }
      
      // If error is not retryable or this was the last attempt, throw it
      if (!retryCondition(lastError, null) || attempt === maxRetries + 1) {
        throw lastError;
      }
    }
    
    // Calculate delay for next attempt
    if (attempt <= maxRetries) {
      let delay = Math.min(baseDelay * Math.pow(backoffMultiplier, attempt - 1), maxDelay);
      
      // Add jitter to prevent thundering herd
      if (jitter) {
        delay = delay * (0.5 + Math.random() * 0.5);
      }
      
      // Special handling for rate limits - longer delay
      if (lastResponse && lastResponse.status === 429) {
        const retryAfter = lastResponse.headers.get('Retry-After');
        if (retryAfter) {
          delay = Math.max(delay, parseInt(retryAfter) * 1000);
        } else {
          delay = Math.max(delay, 5000); // 5 second minimum for rate limits
        }
      }
      
      logger.log(`Waiting ${Math.round(delay)}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  // This should never be reached, but just in case
  throw lastError || new NetworkError('Maximum retries exceeded');
}

// Specialized retry for auth operations
export async function authFetch(url, options = {}, config = {}) {
  const authConfig = {
    maxRetries: 2, // Fewer retries for auth (don't want to spam)
    baseDelay: 1000,
    timeout: 10000, // Shorter timeout for auth
    retryCondition: (error, response) => {
      // Retry on network errors
      if (error && error.retryable) {
        return true;
      }
      // Retry on 5xx errors
      if (response && response.status >= 500 && response.status < 600) {
        return true;
      }
      // Don't retry on auth errors (4xx except 429)
      if (response && response.status >= 400 && response.status < 500 && response.status !== 429) {
        return false;
      }
      // Retry on 429 (rate limit)
      if (response && response.status === 429) {
        return true;
      }
      return false;
    },
    ...config
  };
  
  return fetchWithRetry(url, options, authConfig);
}

// Network status detection
export class NetworkMonitor {
  constructor() {
    this.isOnline = navigator.onLine;
    this.listeners = new Set();
    
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
  }
  
  handleOnline() {
    logger.log('Network connection restored');
    this.isOnline = true;
    this.notifyListeners({ isOnline: true, type: 'online' });
  }
  
  handleOffline() {
    logger.log('Network connection lost');
    this.isOnline = false;
    this.notifyListeners({ isOnline: false, type: 'offline' });
  }
  
  addListener(callback) {
    this.listeners.add(callback);
    // Immediately notify of current status
    callback({ isOnline: this.isOnline, type: 'status' });
    
    // Return unsubscribe function
    return () => this.listeners.delete(callback);
  }
  
  notifyListeners(event) {
    this.listeners.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        logger.error('Error in network status listener:', error);
      }
    });
  }
  
  // Test network connectivity with a quick request
  async testConnectivity() {
    try {
      const response = await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000)
      });
      return response.ok;
    } catch {
      return false;
    }
  }
}

// Create singleton instance
export const networkMonitor = new NetworkMonitor();